import { ColDef, ColGroupDef, GridSizeChangedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Box } from "@mui/material";
import {
  DataTableV2,
  defaultColDef,
  paletteVar,
  paletteVarAlpha,
} from "@platform/design-system";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";

import { ScenarioLOC } from "../types";
import { SCENARIO_DETAILS_COLUMN_HEADER } from "../constants";

const applyDefaultColDef = (
  col: ColDef | ColGroupDef,
): ColDef | ColGroupDef => {
  if ("children" in col && col.children) {
    return { ...col, children: col.children.map(applyDefaultColDef) };
  }
  return { ...defaultColDef, ...col, cellStyle: {}, wrapText: false };
};

type ScenarioDetailsTableProps = {
  rows: ScenarioLOC[];
  onSelectionChanged?: (selectedRows: ScenarioLOC[]) => void;
  loading?: boolean;
};

export type ScenarioDetailsTableHandle = {
  clearSelection: () => void;
  getSelectedRows: () => ScenarioLOC[];
};

export const ScenarioDetailsTable = forwardRef<
  ScenarioDetailsTableHandle,
  ScenarioDetailsTableProps
>(({ rows, onSelectionChanged, loading }, ref) => {
  const gridRef = useRef<AgGridReact>(null);

  useImperativeHandle(ref, () => ({
    clearSelection: () => {
      gridRef.current?.api?.deselectAll();
    },
    getSelectedRows: () =>
      (gridRef.current?.api?.getSelectedRows() as ScenarioLOC[]) ?? [],
  }));

  const columns = useMemo(
    () => SCENARIO_DETAILS_COLUMN_HEADER.map(applyDefaultColDef),
    [],
  );

  const handleSelectionChanged = useCallback(
    (event: { api: { getSelectedRows: () => ScenarioLOC[] } }) => {
      const leafRows = event.api.getSelectedRows().filter((row) => row.id);
      onSelectionChanged?.(leafRows);
    },
    [onSelectionChanged],
  );

  const handleGridSizeChanged = useCallback((event: GridSizeChangedEvent) => {
    const allColumns = event.api.getColumns();
    if (!allColumns) return;
    const totalColWidth = allColumns.reduce(
      (sum, col) => sum + col.getActualWidth(),
      0,
    );
    if (totalColWidth < event.clientWidth) {
      event.api.sizeColumnsToFit();
    }
  }, []);

  const gridOptions = useMemo(
    () => ({
      defaultColDef: { flex: undefined, filter: true },
      enableFilterHandlers: true,
      suppressAggFuncInHeader: true,
      autoSizeStrategy: { type: "fitCellContents" as const },
      groupDisplayType: "custom" as const,
      pagination: true,
      paginationPageSize: 25,
      paginationPageSizeSelector: [25, 50, 100],
      rowSelection: {
        mode: "multiRow" as const,
        checkboxes: true,
        headerCheckbox: true,
        selectAll: "filtered" as const,
        groupSelects: "descendants" as const,
        enableClickSelection: false,
      },
      selectionColumnDef: {
        width: 56,
        maxWidth: 56,
        resizable: false,
        suppressHeaderMenuButton: true,
      },
      getRowId: (params: { data: ScenarioLOC }) => params.data.id,
      getRowClass: (params: { node: { group?: boolean } }) =>
        !params.node.group ? "child-row" : undefined,
    }),
    [],
  );

  return (
    <Box
      className="ag-theme-quartz"
      sx={{
        height: "calc(100vh - 300px)",
        minHeight: 400,
        width: "100%",
        "& > div": { height: "100%" },
        "& .ag-cell": {
          borderRight: "1px solid var(--ag-border-color, #dde2eb)",
        },
        ...(rows.length > 0 && {
          "& .ag-center-cols-viewport": {
            minHeight: "unset !important",
          },
        }),
        "& .child-row": {
          backgroundColor: paletteVarAlpha(
            paletteVar("secondary-main", "#0078A2"),
            0.03,
          ),
        },
        "& .ag-row-selected::before": {
          background: "transparent",
        },
        "& .ag-row-selected .ag-cell-range-selected": {
          background: "transparent",
        },
      }}
    >
      <DataTableV2
        ref={gridRef}
        domLayout="normal"
        rowData={rows}
        columnDefs={columns}
        gridOptions={gridOptions}
        onSelectionChanged={handleSelectionChanged}
        onGridSizeChanged={handleGridSizeChanged}
        loading={loading}
        sideBar={{
          toolPanels: [
            {
              id: "columns",
              labelDefault: "Columns",
              labelKey: "columns",
              iconKey: "columns",
              toolPanel: "agColumnsToolPanel",
              toolPanelParams: {
                suppressSyncLayoutWithGrid: true,
                suppressRowGroups: true,
                suppressValues: true,
                suppressPivots: true,
                suppressPivotMode: true,
              },
            },
            {
              id: "filters",
              labelDefault: "Filters",
              labelKey: "filters",
              iconKey: "filter",
              toolPanel: "agNewFiltersToolPanel",
              toolPanelParams: {
                suppressSyncLayoutWithGrid: true,
              },
            },
          ],
        }}
      />
    </Box>
  );
});

ScenarioDetailsTable.displayName = "ScenarioDetailsTable";
